import { useState } from 'react';
import { useTheme } from '../../context/ThemeContext.jsx';
import PreviewTab from './PreviewTab.jsx';
import WaterfallTab from './WaterfallTab.jsx';
import GlyphMapTab from './GlyphMapTab.jsx';
import OpenTypeTab from './OpenTypeTab.jsx';
import VariableTab from './VariableTab.jsx';
import StarRating from '../StarRating.jsx';
import SimilarFonts from '../SimilarFonts.jsx';
import FontPairings from '../FontPairings.jsx';
import LicenseSummary from '../LicenseSummary.jsx';

const TABS = [
  { id: 'preview', label: 'Preview' },
  { id: 'waterfall', label: 'Waterfall' },
  { id: 'glyphs', label: 'Glyphs' },
  { id: 'opentype', label: 'OT Features' },
  { id: 'variable', label: 'Variable', requiresVariable: true },
];

export default function DetailPanel({
  font, onClose, previewText, setPreviewText,
  toggleActivation, deleteFont, tags, onAddTag, onRemoveTag,
  showDetailTagDropdown, setShowDetailTagDropdown,
  collections, onSelectFont, onRate, onDownload,
}) {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState('preview');

  if (!font) return null;

  const isVariable = font.is_variable || (font.variable_axes && (typeof font.variable_axes === 'string' ? JSON.parse(font.variable_axes) : font.variable_axes)?.length > 0);
  const visibleTabs = TABS.filter(t => !t.requiresVariable || isVariable);

  const renderTab = () => {
    switch (activeTab) {
      case 'preview': return <PreviewTab font={font} previewText={previewText} setPreviewText={setPreviewText} />;
      case 'waterfall': return <WaterfallTab font={font} previewText={previewText} />;
      case 'glyphs': return <GlyphMapTab font={font} />;
      case 'opentype': return <OpenTypeTab font={font} previewText={previewText} />;
      case 'variable': return <VariableTab font={font} previewText={previewText} />;
      default: return null;
    }
  };

  return (
    <aside style={{
      width: 360, borderLeft: `1px solid ${theme.border}`,
      padding: 20, overflowY: 'auto',
      background: theme.bgElevated,
      height: '100%', boxSizing: 'border-box',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 16,
      }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: theme.text }}>{font.name}</h3>
        <button
          onClick={onClose}
          style={{
            background: theme.bgInput, border: 'none',
            borderRadius: 6, color: theme.textMuted,
            width: 26, height: 26, cursor: 'pointer', fontSize: 12,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >✕</button>
      </div>

      {/* Star Rating */}
      <div style={{ marginBottom: 12 }}>
        <StarRating
          rating={font.user_rating}
          avgRating={font.avg_rating}
          ratingCount={font.rating_count}
          onRate={(r) => onRate?.(font.id, r)}
          size={18}
          interactive
        />
      </div>

      {/* Tab Bar */}
      <div style={{
        display: 'flex', gap: 2, marginBottom: 16,
        background: theme.bgInput, borderRadius: 8, padding: 3,
        overflowX: 'auto',
      }}>
        {visibleTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              flex: 1, padding: '6px 4px',
              background: activeTab === tab.id ? theme.accentBg : 'transparent',
              border: activeTab === tab.id ? `1px solid ${theme.accentBorder}` : '1px solid transparent',
              borderRadius: 6,
              color: activeTab === tab.id ? theme.accent : theme.textMuted,
              fontSize: 10, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'inherit', whiteSpace: 'nowrap',
            }}
          >{tab.label}</button>
        ))}
      </div>

      {/* Active Tab Content */}
      <div style={{ marginBottom: 20 }}>
        {renderTab()}
      </div>

      {/* Metadata */}
      <div style={{ marginBottom: 20 }}>
        <div style={{
          fontSize: 10, fontWeight: 600, color: theme.textFaint,
          marginBottom: 8, letterSpacing: '0.06em', textTransform: 'uppercase',
        }}>Details</div>
        {[
          ['Designer', font.designer],
          ['Category', font.category],
          ['Styles', `${font.style_count} styles`],
          ['License', font.license_type !== 'unknown' ? font.license_type : font.license],
          ['File Size', font.size_display],
          ['Source', font.source],
          font.license_seats && ['Seats', font.license_seats],
          font.license_expiry && ['Expires', new Date(font.license_expiry).toLocaleDateString()],
        ].filter(Boolean).filter(([_, v]) => v).map(([k, v]) => (
          <div key={k} style={{
            display: 'flex', justifyContent: 'space-between',
            fontSize: 12, padding: '4px 0',
            borderBottom: `1px solid ${theme.borderLight}`,
          }}>
            <span style={{ color: theme.textMuted }}>{k}</span>
            <span style={{ color: theme.textSecondary, fontWeight: 500 }}>{v}</span>
          </div>
        ))}
      </div>

      {/* Font Files */}
      {font.files?.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{
            fontSize: 10, fontWeight: 600, color: theme.textFaint,
            marginBottom: 8, letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>Files ({font.files.length})</div>
          {font.files.map(f => (
            <div key={f.id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              fontSize: 11, padding: '4px 0',
              borderBottom: `1px solid ${theme.borderLight}`,
            }}>
              <span style={{ color: theme.textTertiary }}>
                {f.weight} {f.style} <span style={{ color: theme.textGhost }}>.{f.format}</span>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Tags */}
      <div style={{ marginBottom: 20 }}>
        <div style={{
          fontSize: 10, fontWeight: 600, color: theme.textFaint,
          marginBottom: 8, letterSpacing: '0.06em', textTransform: 'uppercase',
        }}>Tags</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {(font.tags || []).map(t => {
            const isAuto = t.is_auto || t.source === 'auto';
            return (
              <span key={t.id} style={{
                fontSize: 11, fontWeight: 500,
                color: t.color || theme.accent,
                background: `${t.color || theme.accent}18`,
                padding: '4px 10px', borderRadius: 5,
                border: `1px ${isAuto ? 'dashed' : 'solid'} ${t.color || theme.accent}30`,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                {isAuto && <span style={{ fontSize: 8, opacity: 0.6, fontWeight: 700, textTransform: 'uppercase' }}>auto</span>}
                {t.name}
                <span
                  onClick={() => onRemoveTag(t.id)}
                  style={{ cursor: 'pointer', fontSize: 10, opacity: 0.7 }}
                >{'\u2715'}</span>
              </span>
            );
          })}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowDetailTagDropdown(!showDetailTagDropdown)}
              style={{
                fontSize: 11, color: theme.accent,
                background: theme.accentBg,
                border: `1px dashed ${theme.accentBorder}`,
                borderRadius: 5, padding: '4px 10px',
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >+ Add</button>
            {showDetailTagDropdown && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, marginTop: 4,
                background: theme.bgElevated, border: `1px solid ${theme.borderHover}`,
                borderRadius: 8, padding: 4, minWidth: 140, zIndex: 60,
                boxShadow: theme.shadow,
              }}>
                {tags.filter(t => !(font.tags || []).some(ft => ft.id === t.id)).length === 0 && (
                  <div style={{ padding: '6px 10px', fontSize: 11, color: theme.textFaint }}>
                    {tags.length === 0 ? 'No tags created' : 'All tags assigned'}
                  </div>
                )}
                {tags.filter(t => !(font.tags || []).some(ft => ft.id === t.id)).map(t => (
                  <button
                    key={t.id}
                    onClick={() => onAddTag(t.id)}
                    style={{
                      width: '100%', padding: '6px 10px', background: 'none',
                      border: 'none', borderRadius: 5, color: theme.textSecondary,
                      fontSize: 11, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = theme.bgHover}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                  >
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: t.color }} />
                    {t.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Collections */}
      <div style={{ marginBottom: 20 }}>
        <div style={{
          fontSize: 10, fontWeight: 600, color: theme.textFaint,
          marginBottom: 8, letterSpacing: '0.06em', textTransform: 'uppercase',
        }}>Collections</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {(font.collections || []).map(c => (
            <span key={c.name || c} style={{
              fontSize: 11, fontWeight: 500, color: theme.textTertiary,
              background: theme.badgeBg, padding: '4px 10px', borderRadius: 5,
              border: `1px solid ${theme.border}`,
            }}>{c.name || c}</span>
          ))}
        </div>
      </div>

      {/* Similar Fonts */}
      <SimilarFonts fontId={font.id} onSelectFont={onSelectFont} />

      {/* Font Pairings */}
      <FontPairings fontId={font.id} onSelectFont={onSelectFont} />

      {/* AI License Summary */}
      <LicenseSummary fontId={font.id} license={font.license} licenseType={font.license_type} />

      {/* Actions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <button
          onClick={() => toggleActivation(font.id)}
          style={{
            width: '100%', padding: 10,
            background: font.is_activated ? theme.redBg : 'linear-gradient(135deg, #4F8EF7, #7C5CFC)',
            border: font.is_activated ? `1px solid ${theme.redBorder}` : 'none',
            borderRadius: 8,
            color: font.is_activated ? theme.red : '#fff',
            fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
          }}
        >{font.is_activated ? 'Deactivate Font' : 'Activate Font'}</button>

        <button
          onClick={() => onDownload?.(font.id)}
          style={{
            width: '100%', padding: 10,
            background: theme.bgInput,
            border: `1px solid ${theme.borderMedium}`,
            borderRadius: 8, color: theme.textTertiary,
            fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
          }}
        >Download Original Files</button>

        <button
          onClick={() => { if (confirm(`Delete ${font.name}?`)) deleteFont(font.id); }}
          style={{
            width: '100%', padding: 10, background: 'transparent',
            border: `1px solid ${theme.redBorder}`,
            borderRadius: 8, color: `${theme.red}99`,
            fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
          }}
        >Delete Font</button>
      </div>
    </aside>
  );
}
